import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { EmptyIndicator, LoadingIndicator } from 'components/common';
import { BoxRow } from 'components/common/box';
import { ParticipantsStats } from 'components/common/stats';
import { CourseTitleWithInfo } from 'components/courses/common';
import { MyTeamFollowBox, useMyTeamFollowContext } from '.';

import type {
  GetTeamCoursesCategoriesRes,
  GetTeamCoursesRes
} from 'shared-types/responses';

const MyTeamCourses = () => {
  const { queryString, queryParams } = useMyTeamFollowContext();

  const { messages, theme, user } = useReduxState();

  const { data, loading } = useApiData<GetTeamCoursesRes>({
    url:
      `/admin/supervisors/${user.id}/team-courses` +
      (queryString.length ? `?${queryString}` : ''),
    onlyIf: Boolean(user.id)
  });

  const { data: courseCategories } = useApiData<GetTeamCoursesCategoriesRes>({
    url: `/admin/supervisors/${user.id}/team-courses-categories`,
    onlyIf: Boolean(user?.id)
  });

  const customFilters = [
    ...(courseCategories?.categories?.length
      ? [
          {
            allowMultiple: true,
            field: 'categoryId',
            icon: 'tag' as const,
            items: courseCategories.categories.map((category) => ({
              label: category.title,
              value: `${category.id}`
            })),
            position: 0,
            title: messages.commonCategory
          }
        ]
      : []),
    {
      field: 'isMandatory',
      icon: 'exclamation-circle' as const,
      items: [
        { label: messages.commonYes, value: 'true' },
        { label: messages.commonNo, value: 'false' }
      ],
      position: 1,
      title: messages.commonMandatory
    }
  ];

  return (
    <MyTeamFollowBox
      numberOfPages={data?.courses?.length ? data?.pages || 1 : 0}
      title={messages.commonCourses}
      {...(customFilters.length ? { customFilters } : {})}
    >
      {loading ? (
        <LoadingIndicator />
      ) : data?.courses?.length ? (
        data.courses.map((course) => (
          <BoxRow key={course.id}>
            <div className="row-sm row-center">
              <div className="col">
                <CourseTitleWithInfo
                  course={course}
                  linkHref={`/my-team/follow/courses/${course.id}`}
                >
                  {course.mandatoryStatus ? (
                    <>
                      <span className="mandatory">
                        {course.mandatoryStatus === 'some'
                          ? messages.commonMandatoryForSome
                          : messages.commonMandatory}
                      </span>
                      <span className="divider">{'•'}</span>
                    </>
                  ) : null}
                  {getMessageWithPlaceholders({
                    message: messages.courseNumberOfChapters,
                    placeholders: { numberOfChapters: course.numberOfChapters }
                  })}
                </CourseTitleWithInfo>
              </div>

              <div className="col-min">
                <ParticipantsStats
                  completionEnabled={course.completionEnabled}
                  progressData={course.progressData}
                  notStartedLabel={
                    queryParams['participantsOnly'] === 'true'
                      ? messages.tasksUsersNotStartedSingular
                      : messages.commonNotStartedOrNotAdded
                  }
                />
              </div>
            </div>
          </BoxRow>
        ))
      ) : (
        <EmptyIndicator icon="book" title={messages.commonNoCourses} />
      )}

      <style jsx>{`
        .mandatory {
          color: ${theme.dangerColor};
        }
        .divider {
          margin: 0 7px;
        }
      `}</style>
    </MyTeamFollowBox>
  );
};

export default MyTeamCourses;
